import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  DOCUMENT,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import {
  provideHlmDatePickerConfig,
  provideHlmDatePickerMultiConfig,
  provideHlmDateRangePickerConfig,
} from '@spartan-ng/helm/date-picker';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslocoTitleStrategy } from './core/i18n/transloco-title.strategy';
import {
  documentLangFor,
  isAppLocale,
  SUPPORTED_LANGS,
} from './core/i18n/locale';
import { API_URL } from './core/services/api.tokens';
import { TranslocoHttpLoader } from './transloco-loader';
import { AuthStore } from './core/stores/auth.store';
import { authInterceptor } from './core/interceptors/auth.interceptor';

function formatDateForActiveLocale(date: Date): string {
  const lang =
    typeof document !== 'undefined' && document.documentElement.lang
      ? document.documentElement.lang
      : 'fr-FR';
  return date.toLocaleDateString(lang, { dateStyle: 'short', timeZone: 'UTC' });
}

function initializeLocale(translocoService: TranslocoService, cookieService: CookieService): void {
  translocoService.setAvailableLangs([...SUPPORTED_LANGS]);
  translocoService.setDefaultLang('fr');

  if (cookieService.check('locale')) {
    const savedLocale = cookieService.get('locale');
    if (isAppLocale(savedLocale)) {
      translocoService.setActiveLang(savedLocale);
      document.documentElement.lang = documentLangFor(savedLocale);
      return;
    }
    cookieService.set('locale', translocoService.getDefaultLang());
    document.documentElement.lang = 'fr-FR';
    return;
  }

  let browserLocale = 'fr';
  if (typeof navigator !== 'undefined' && navigator.language) {
    browserLocale = navigator.language.split('-')[0];
  }

  if (isAppLocale(browserLocale)) {
    translocoService.setActiveLang(browserLocale);
    cookieService.set('locale', browserLocale);
  } else {
    cookieService.set('locale', translocoService.getDefaultLang());
  }

  const active = translocoService.getActiveLang();
  document.documentElement.lang = documentLangFor(isAppLocale(active) ? active : 'fr');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    { provide: API_URL, useValue: environment.apiUrl },
    provideHttpClient(withInterceptors([authInterceptor])),
    provideTranslocoLocale({
      langToLocaleMapping: {
        en: 'en-US',
        fr: 'fr-FR',
      },
      localeConfig: {
        global: {
          date: {
            dateStyle: 'short',
            timeStyle: 'short',
            timeZone: 'UTC',
          },
        },
      },
    }),
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGS],
        defaultLang: 'fr',
        // Keep scope folder names as-is (e.g. assessment-forms) so keys match templates.
        scopes: { keepCasing: true },
        missingHandler: {
          allowEmpty: true,
          logMissingKey: isDevMode(),
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideHlmDatePickerConfig({
      autoCloseOnSelect: true,
      transformDate: (date: Date) => date,
      formatDate: (date: Date) => formatDateForActiveLocale(date),
    }),
    provideHlmDateRangePickerConfig({
      autoCloseOnEndSelection: true,
      transformDates: (dates: [Date, Date]) => dates,
      formatDates: (dates: [Date | null, Date | null]) => {
        const start = dates[0] ? formatDateForActiveLocale(dates[0]) : '';
        const end = dates[1] ? formatDateForActiveLocale(dates[1]) : '';
        return `${start} - ${end}`.trim();
      },
    }),
    provideHlmDatePickerMultiConfig({
      autoCloseOnMaxSelection: true,
      transformDates: (dates: Date[]) => dates,
      formatDates: (dates: Date[]) =>
        dates.map((date) => formatDateForActiveLocale(date)).join(', '),
    }),
    CookieService,
    provideAppInitializer(() => {
      const authStore = inject(AuthStore);
      const translocoService = inject(TranslocoService);
      const cookieService = inject(CookieService);
      const document = inject(DOCUMENT);

      initializeLocale(translocoService, cookieService);

      if (cookieService.check('theme')) {
        const savedTheme = cookieService.get('theme');
        if (savedTheme === 'dark') {
          document.documentElement.classList.add('dark');
          document.documentElement.classList.remove('light');
        } else {
          document.documentElement.classList.add('light');
          document.documentElement.classList.remove('dark');
        }
      } else {
        cookieService.set('theme', 'light');
        document.documentElement.classList.add('light');
        document.documentElement.classList.remove('dark');
      }

      return firstValueFrom(authStore.initialize());
    }),
  ],
};
